import React, { useState, useEffect } from 'react'
import { isEmpty, size } from 'lodash'

import { HelpCircle as HelpCircleIcon } from 'react-feather'
import { Alert } from '@material-ui/lab'
import {
  Box,
  Button,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core'

import {
  Page,
  Container,
  LoadingFeedback,
  ContentHeader,
  TutorialTrails,
  DriveTour,
} from 'components'
import {
  QuestionnaireCard,
  AdequacyModal,
  LevelAlert,
  NextLevelModal,
} from './components'

import useFetch from 'hooks/useFetch'
import useFilter from 'hooks/useFilter'
import useAuth from 'hooks/useAuth'

import helpers from 'helpers'
import * as service from 'service'
import constants from 'constants/index'
import theme from 'theme'

const QuestionnairesMain = () => {
  const {
    COMPLIANCE_LEVEL_NOT_STARTED,
    COMPLIANCE_LEVEL_THREE,
    FUNDAMENTAL_REQUIREMENTS,
    LEVEL_CONFIG,
    QUESTIONNAIRE_MAIN_DRIVER_STEPS_OBJECT,
    QUESTIONNAIRE_MAIN_DRIVER_STEP_1,
    QUESTIONNAIRE_MAIN_DRIVER_STEP_2,
  } = constants.questionnaire

  const [openModal, setOpenModal] = useState(false)
  const [openNextLevelModal, setOpenNextLevelModal] = useState(false)
  const [unlockLevel, setUnlockLevel] = useState(false)
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState(0)
  const [openTour, setOpenTour] = useState(false)

  const filter = useFilter()
  const { company } = useAuth()

  const { mixpanel } = helpers

  const referrer = document.referrer
  const params = new URL(referrer || 'https://app.dponet.com.br')

  const { response, isLoading, refresh } = useFetch(
    service.dponet.questionnaires.get,
    {
      perPage: 9999,
      hideQuestions: true,
      ...{ ...filter.filters },
    },
    [filter.filters],
  )

  const {
    response: responseQuestionnaireLevel,
    isLoading: isLoadingQuestionnaireLevel,
    refresh: refreshQuestionnaireLevel,
  } = useFetch(service.dponet.companies.questionnaireComplianceLevel, {
    companyId: company.id,
  })

  const questionnaireLevelInfo = responseQuestionnaireLevel?.data?.company

  const fundamentalQuestionnaire = response?.data?.questionnaires?.find(
    (questionnaire) => questionnaire.kind === FUNDAMENTAL_REQUIREMENTS,
  )

  const nonCompliance =
    Number(fundamentalQuestionnaire?.complianceGap ?? 100) < 100

  const zeroQuestionnaires = isEmpty(response?.data?.questionnaires)

  const isDisabledUnlock =
    questionnaireLevelInfo?.conformityLevel === COMPLIANCE_LEVEL_NOT_STARTED

  const handleOpenNextLevelModal = () => {
    setUnlockLevel(true)
    setOpenNextLevelModal(true)
  }

  const TUTORIAL_TRAILS_STEPS = [1, 2, 3].map((level) => {
    const concluded = questionnaireLevelInfo?.conformityLevel > level - 1

    return {
      ...LEVEL_CONFIG[level],
      concluded,
      disabled: isDisabledUnlock,
      ...(!concluded &&
        !isDisabledUnlock && {
          action: (
            <Button variant="contained" onClick={handleOpenNextLevelModal}>
              Desbloquear
            </Button>
          ),
        }),
    }
  })

  const handleRequestRefresh = () => {
    refresh()
    refreshQuestionnaireLevel()
  }

  const handleClose = () => {
    mixpanel.track(
      'Jornada de Fornecedores',
      'Modal: Adequação pendências de medidas de controle importantes',
      {
        action: 'Fechar-Adequação',
      },
    )
    setOpenModal(false)
  }

  const handleChangeStep = (stepIndex) => setStep(stepIndex)

  const handleOpenTour = () => {
    const where = 'Questionários'
    const title = 'Tour'

    const params = {
      action: 'tour-questionários',
    }

    helpers.mixpanel.track(where, title, params)

    setOpenTour(true)
  }

  useEffect(() => {
    const searchPath = params.pathname
    if (searchPath === '/supplier-step' && nonCompliance > 0) {
      setOpenModal(true)
    }
    // eslint-disable-next-line
  }, [nonCompliance])

  useEffect(() => {
    const notConcludedIndex = TUTORIAL_TRAILS_STEPS.findIndex(
      (step) => !step.concluded,
    )

    const newStep =
      notConcludedIndex === -1
        ? size(TUTORIAL_TRAILS_STEPS) - 1
        : notConcludedIndex

    setStep(newStep)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireLevelInfo])

  useEffect(() => {
    if (
      questionnaireLevelInfo?.conformityLevelCompleted &&
      questionnaireLevelInfo?.conformityLevel !== COMPLIANCE_LEVEL_THREE &&
      questionnaireLevelInfo?.conformityLevel !== COMPLIANCE_LEVEL_NOT_STARTED
    ) {
      setUnlockLevel(false)
      setOpenNextLevelModal(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireLevelInfo])

  return (
    <Page title="Todos os questionários">
      <LoadingFeedback
        open={isLoading || isLoadingQuestionnaireLevel || loading}
      />
      <Container maxWidth={false}>
        <ContentHeader title="Questionários">
          <Tooltip title="Iniciar o tour guiado">
            <Button
              id={QUESTIONNAIRE_MAIN_DRIVER_STEP_1}
              variant="contained"
              color="primary"
              startIcon={<HelpCircleIcon size={20} />}
              onClick={handleOpenTour}
              fullWidth
            >
              Tutorial
            </Button>
          </Tooltip>
        </ContentHeader>

        {zeroQuestionnaires && !isLoading && (
          <Alert variant="filled" severity="info" color="warning">
            <Typography>
              Você não tem permissões para visualizar questionários. Entre em
              contato com o responsável ou co-responsável pela empresa e
              solicite a sua permissão.
            </Typography>
          </Alert>
        )}
        {!isLoading && !zeroQuestionnaires && (
          <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
            <LevelAlert
              questionnaireLevelInfo={questionnaireLevelInfo}
              refresh={refresh}
              refreshQuestionnaireLevel={refreshQuestionnaireLevel}
            />
            <Paper variant="outlined">
              <Box
                p={2}
                display="flex"
                flexDirection="column"
                gridGap={theme.spacing(1)}
              >
                <Typography variant="h5">Níveis de conformidade</Typography>
                <TutorialTrails
                  steps={TUTORIAL_TRAILS_STEPS}
                  iframe="https://player.vimeo.com/video/1002706100?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                  currentStep={step}
                  onChange={handleChangeStep}
                  isLoading={isLoadingQuestionnaireLevel}
                />
              </Box>
            </Paper>

            {openNextLevelModal && (
              <NextLevelModal
                companyId={company?.id}
                loading={loading}
                level={step + 1}
                open={openNextLevelModal}
                refresh={handleRequestRefresh}
                setLoading={setLoading}
                setOpen={setOpenNextLevelModal}
                unlockLevel={unlockLevel}
              />
            )}
            <Grid container spacing={2}>
              {response?.data?.questionnaires?.map((questionnaire) => (
                <QuestionnaireCard
                  tourStep={QUESTIONNAIRE_MAIN_DRIVER_STEP_2}
                  key={questionnaire.id}
                  questionnaire={questionnaire}
                />
              ))}
            </Grid>
          </Box>
        )}
      </Container>
      <AdequacyModal
        company={company}
        open={openModal}
        handleClose={handleClose}
        nonCompliance={nonCompliance}
        setOpenModal={setOpenModal}
      />

      <DriveTour
        stepsMatrix={QUESTIONNAIRE_MAIN_DRIVER_STEPS_OBJECT}
        open={openTour}
        setOpen={setOpenTour}
      />
    </Page>
  )
}

export default QuestionnairesMain
